import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useRouteMatch, useParams } from "react-router-dom"
import { Table, Tooltip, Skeleton, Menu, Dropdown, Button, notification } from "antd"
import { DownloadOutlined, PlusOutlined, UploadOutlined, EditOutlined, SearchOutlined, FilterOutlined, DeleteOutlined, DownOutlined } from "@ant-design/icons"

import { fetchLedgerFields, selectLedgerFields, selectTotal, selectPagination, selectLoading, filterLedgerFields, deleteLedgerField, generateCsv } from "../ledgerFieldsSlice"
import { allObjectValuesEmpty } from "../../../../functions/objectFunctions/allObjectValuesEmpty"
import ColumnSearchFilter from "../../../../components/utilityComponents/table/ColumnSearchFilter"
import ColumnHeaderTitle from "../../../../components/utilityComponents/table/ColumnHeaderTitle"
import { tableFiltersApplied } from "../../../../functions/objectFunctions/tableFiltersApplied"
import useConfirmationModal from "../../../alertsPanel/ShowConfirm"

import Role from "../../../userRoles/Role"

export default function LedgerFieldTable({ scopedByClient }) {
    /////*** react router start ***/////
    const { url } = useRouteMatch()
    let { clientId } = useParams()
    /////*** react router end ***/////

    /////*** redux start ***/////
    const dispatch = useDispatch()
    const ledgerFields = useSelector(selectLedgerFields)
    const total = useSelector(selectTotal)
    const pagination = useSelector(selectPagination)
    const loading = useSelector(selectLoading)
    /////*** redux end ***/////

    /////*** useState start ***/////
    const [loaded, setLoaded] = useState(false)
    const [filters, setFilters] = useState({})
    const [sort, setSort] = useState({
        column: "name",
        direction: "asc",
    })
    /////*** useState end ***/////

    /////*** useEffects start ***/////
    useEffect(() => {
        if (!loaded) {
            setLoaded(true)
            handleFetchInitialLedgerFields()
        }
    }, [loaded, url, dispatch, pagination]) // eslint-disable-line
    /////*** useEffects end ***/////

    /////*** event handler methods start ***/////
    const handleFetchInitialLedgerFields = () => {
        dispatch(fetchLedgerFields(clientId, pagination))
    }

    const showConfirm = useConfirmationModal();
    const handleDeleteClick = (id, clientId) => {
        showConfirm(deleteLedgerField, id, clientId);
    };

    const handleFilterLedgerFields = (newPagination = null, newSort = null) => {
        return dispatch(
            filterLedgerFields({
                clientId: clientId,
                filters: filters,
                pagination: !!newPagination ? newPagination : pagination,
                sort: !!newSort ? newSort : sort,
            })
        )
    }

    const handleSortLedgerFieldsChange = (column) => () => {
        let newDirection = sort.direction === "asc" ? "desc" : "asc"
        let newSort = {
            column: column,
            direction: newDirection,
        }
        setSort(newSort)
        handleFilterLedgerFields(null, newSort)
    }

    const handleTableChange = (newPagination, sorter) => {
        if (allObjectValuesEmpty(filters) && allObjectValuesEmpty(sorter)) {
            dispatch(fetchLedgerFields(clientId, newPagination))
        } else {
            handleFilterLedgerFields(newPagination)
        }
    }
    /////*** event handler methods end ***/////

    const renderColumnHeaderTitle = (title) => {
        return <ColumnHeaderTitle title={title} handleSortChange={handleSortLedgerFieldsChange} sort={sort} />
    }

    const renderResetFilters = () => {
        if (allObjectValuesEmpty(filters)) {
            return null
        }
        return (
            <div className={"actions-alternate-buttons"}>
                <a
                    href="javascript:;"
                    onClick={() => {
                        handleFetchInitialLedgerFields()
                        setFilters({})
                        setSort({ column: "name", direction: "asc" })
                    }}>
                    <FilterOutlined />
                    Reset Filters
                </a>
            </div>
        )
    }

    const accountFieldCsv = () => {
        notification.open({
            message: "Ledger fields export started",
            duration: 0.8,
        })

        dispatch(generateCsv(clientId, filters))
    }

    const renderSearchOutlinedButton = (filterKey) => {
        return <SearchOutlined style={{ fontSize: "16px", color: tableFiltersApplied(filterKey, filters) ? "#1890ff" : undefined }} />
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>Ledger Fields</h2>
                            <div className="badge">{total}</div>
                        </div>
                        <div className="actions">
                            <div className="table-action-links">
                                {renderResetFilters()}

                                <Role action="create" model="ledger_field">
                                    <Link to={`${url}/new`}>
                                        <PlusOutlined />
                                        Add New
                                    </Link>
                                </Role>

                                <Link to={`${url}/import`}>
                                    <PlusOutlined />
                                    Import
                                </Link>

                                <a href="javascript:;" onClick={() => accountFieldCsv()}>
                                    <DownloadOutlined />
                                    Export
                                </a>
                            </div>
                        </div>
                    </div>

                    <Skeleton active loading={loading}>
                        <Table dataSource={ledgerFields} pagination={{ ...pagination, showSizeChanger: true, total: total }} onChange={handleTableChange} rowKey={(r) => r.id}>
                            <Table.Column
                                title={renderColumnHeaderTitle("name")}
                                key="name"
                                dataIndex="name"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialLedgerFields}
                                        filterAction={filterLedgerFields}
                                        filterColumn={"name"}
                                        filterType={"name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("name_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("description")}
                                key="description"
                                dataIndex="description"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialLedgerFields}
                                        filterAction={filterLedgerFields}
                                        filterColumn={"description"}
                                        filterType={"description_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("description_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("order")}
                                key="order"
                                dataIndex="order"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialLedgerFields}
                                        filterAction={filterLedgerFields}
                                        filterColumn={"order"}
                                        filterType={"order_eq"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("order_eq")}
                            />
                            <Table.Column
                                title="Edit"
                                key="action"
                                render={(text, record) => (
                                    <>
                                        <Dropdown
                                            overlay={
                                                <Menu>
                                                    <Role action="update" model="ledger_field">
                                                        <Menu.Item key="1" icon={<EditOutlined />}>
                                                            <Link to={`${url}/${record.id}/edit`}>Edit Ledger Field</Link>
                                                        </Menu.Item>
                                                        <Menu.Item key="2" icon={<DeleteOutlined />} onClick={() => handleDeleteClick(record.id, clientId)}>
                                                            Delete Ledger Field
                                                        </Menu.Item>
                                                    </Role>
                                                    {
                                                        scopedByClient && (
                                                            <Menu.Item key="3">
                                                                <Link to={`${url}/${record.id}/dashboard`}>View</Link>
                                                            </Menu.Item>
                                                        )
                                                    }
                                                </Menu>
                                            }>
                                            <Button>
                                                Actions <DownOutlined />
                                            </Button>    
                                        </Dropdown>
                                    </>
                                )}
                            />
                        </Table>
                    </Skeleton>
                </div>
            </div>
            {/* <div className="card-footer">
                <div className="card-footer-left"></div>
                <div className="card-footer-right pagination"></div>
            </div> */}
        </div>
    )
}
